import { useTranslation } from 'react-i18next';
import { orderAmounts } from './orders.js';
import classes from './Purchasing.module.css';
import { formatAmount } from './requisitions.js';
import type { PurchaseOrderDetail } from './types.js';

/**
 * Siparişin tutar özeti (ADR 0040 #189, M12c-R4; D8, sahip 2026-11-05, ADR 0167 §1): kalemler,
 * masraflar, toplam, teslim alınan ve kalan. Short close'da sipariş tutarı ayrı satırda kalır,
 * toplam teslim edilen tutara iner ve bir satır açıklama düşülür. Eski sunucu sipariş tutarını
 * göndermez: yalnız toplam.
 */
export function PurchaseOrderTotals({
  order,
}: {
  order: Pick<
    PurchaseOrderDetail,
    | 'currency'
    | 'itemsTotal'
    | 'charges'
    | 'orderedTotal'
    | 'total'
    | 'receivedValue'
    | 'remainingValue'
  >;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string) => t(`purchasing.po.detail.${key}`);
  const money = (value: number) => formatAmount(value, locale);
  const amounts = orderAmounts(order);
  return (
    <>
      <dl className={classes.totals}>
        <div>
          <dt>{k('itemsTotal')}</dt>
          <dd>{money(order.itemsTotal)}</dd>
        </div>
        {(['discount', 'freight', 'customs', 'tax', 'serviceFee'] as const)
          .filter((charge) => order.charges[charge] !== 0)
          .map((charge) => (
            <div key={charge}>
              <dt>{k(charge)}</dt>
              <dd>
                {money(charge === 'discount' ? -order.charges[charge] : order.charges[charge])}
              </dd>
            </div>
          ))}
        {amounts.shortClosed ? (
          <div data-testid="po-ordered">
            <dt>{k('ordered')}</dt>
            <dd>{money(amounts.ordered)}</dd>
          </div>
        ) : null}
        <div data-strong>
          <dt>{k('total')}</dt>
          <dd>{`${money(order.total)} ${order.currency}`}</dd>
        </div>
        <div>
          <dt>{k('received')}</dt>
          <dd>{money(order.receivedValue)}</dd>
        </div>
        <div>
          <dt>{k('remaining')}</dt>
          <dd>{money(order.remainingValue)}</dd>
        </div>
      </dl>
      {amounts.shortClosed ? <p className={classes.totalsNote}>{k('shortClosed')}</p> : null}
    </>
  );
}
